/**
 * Locations Repository
 * Client-side repository for fetching countries and states data
 */
"use client";
import {createApiClient, type HttpClientInstance} from "@/infrastructure/http";

import type {CountriesResponse, StatesResponse} from "./types";

class LocationsRepositoryClass {
    private apiClient: HttpClientInstance;

    constructor() {
        this.apiClient = createApiClient();
    }

    /**
     * Get all countries with optional states for specified countries
     *
     * @param includeStates - Country codes to include states for (e.g. ["US"] or ["US", "CA"])
     * @returns Countries list and states for requested countries
     *
     * @example
     * // Get countries with US states (default for billing forms)
     * const data = await locationsRepository().getCountries(["US"]);
     */
    async getCountries(includeStates: Array<string> = []): Promise<CountriesResponse> {
        const params = new URLSearchParams();

        if (includeStates.length > 0) {
            params.set("include_states", includeStates.join(","));
        }

        const url = `/locations/countries${params.toString() ? `?${params}` : ""}`;
        return this.apiClient.get<CountriesResponse>(url);
    }

    /**
     * Get states for a specific country
     *
     * @param countryCode - ISO country code (e.g. "US", "CA")
     * @returns States/provinces for the country
     *
     * @example
     * const states = await locationsRepository().getStates("CA"); // Canadian provinces
     */
    async getStates(countryCode: string): Promise<StatesResponse> {
        const code = countryCode.toUpperCase();
        return this.apiClient.get<StatesResponse>(`/locations/countries/${code}/states`);
    }
}

export function locationsRepository(): LocationsRepositoryClass {
    return new LocationsRepositoryClass();
}
